// The size / background / people controls of the Studio Editor's left panel. Presentational: the page owns the state.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconBlendMode, IconCheck, IconChevronDown, IconPalette, IconPhotoEdit, IconRectangle, IconRectangleVertical, IconUser, IconUsers, IconUsersGroup } from '@tabler/icons-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PHOTO_PROMPT_MAX_CHARS, PHOTO_PROMPT_MAX_WORDS, hasCommandContent, sanitizePhotoPrompt } from '../../../utils/sanitize';
import { BG_BLENDS, BG_TABS, SCENE_CATEGORIES, SOLID_BACKGROUNDS, STUDIO_SIZES, KEEP_BG, bgTabOf, isBgToken, isHex, numFor, parseSize, sizeKey, studioBgCss } from './studioConstants';

/** A titled block of the panel: a step number (or the brand bar), the title, and an optional element on the right. */
export function Section({ title, step, aside, children, className }) {
    return (
        <section className={className}>
            <div className="mb-2.5 flex items-center gap-2">
                {step != null ? (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">{step}</span>
                ) : (
                    <span className="h-4 w-1 shrink-0 rounded-full bg-primary" />
                )}
                <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{title}</h3>
                {aside && <div className="ml-auto flex items-center gap-1.5">{aside}</div>}
            </div>
            {children}
        </section>
    );
}

const tileCls = (selected, disabled) => cn(
    'relative flex h-10 w-full items-center justify-center rounded-lg border-2 transition-colors',
    selected
        ? 'border-primary bg-primary/5 text-primary'
        : 'border-zinc-200 bg-white text-zinc-500 hover:border-primary hover:text-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
);

function CheckDot() {
    return (
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white shadow-sm">
            <IconCheck size={10} strokeWidth={3.5} />
        </span>
    );
}

const inches = (mm) => String(Math.round((mm / 25.4) * 10) / 10);

/** A tiny photo print in the size's own proportions, scaled to fill its box so every shape reads clearly. */
function PrintPreview({ w, h, box = 40, selected }) {
    const scale = box / Math.max(w, h);
    return (
        <span className={cn('flex h-14 w-14 shrink-0 items-center justify-center rounded-lg transition-colors', selected ? 'bg-primary/10' : 'bg-zinc-100 dark:bg-zinc-800')}>
            <span
                className={cn(
                    'relative block overflow-hidden rounded-[3px] border-2 border-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] dark:border-zinc-200',
                    selected ? 'bg-gradient-to-b from-orange-200 to-orange-400' : 'bg-gradient-to-b from-sky-200 to-sky-400',
                )}
                style={{ width: Math.round(w * scale), height: Math.round(h * scale) }}
            >
                {/* a little person, so it reads as a photo */}
                <span className="absolute bottom-0 left-1/2 h-[40%] w-[54%] -translate-x-1/2 rounded-t-full bg-white/75" />
                <span className="absolute bottom-[38%] left-1/2 aspect-square w-[28%] -translate-x-1/2 rounded-full bg-white/85" />
            </span>
        </span>
    );
}

/** Portrait / landscape switch. `onChange(isLandscape)`. */
// `fit`: as wide as its two labels (e.g. in a header row) instead of filling the width.
export function OrientationToggle({ landscape, onChange, disabled = false, fit = false }) {
    const { t } = useTranslation();
    return (
        <div className={cn('grid-cols-2 gap-0.5 rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800/60', fit ? 'inline-grid' : 'grid')} role="radiogroup">
            {[false, true].map((isLandscape) => {
                const active = landscape === isLandscape;
                const Icon = isLandscape ? IconRectangle : IconRectangleVertical;
                return (
                    <button
                        key={isLandscape ? 'landscape' : 'portrait'}
                        type="button"
                        role="radio"
                        disabled={disabled}
                        aria-checked={active}
                        onClick={() => onChange(isLandscape)}
                        className={cn(
                            'inline-flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                            fit && 'px-3',
                            active ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200',
                        )}
                    >
                        <Icon size={15} />
                        {t(isLandscape ? 'imageGeneration.landscape' : 'imageGeneration.portrait')}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Every print size as one row: a true-scale print preview, the name, what it is used for and its mm / inch size. The current
 * orientation is kept (it follows the uploaded photo). Shared by the size panel and the size popup.
 */
export function SizeOptions({ size, onSelect, disabled = false, className }) {
    const { t, i18n } = useTranslation();
    const num = numFor(i18n.language);
    const parsed = parseSize(size);
    const landscape = !!parsed?.landscape;
    return (
        <div className={cn('grid gap-2', className)} role="radiogroup">
            {STUDIO_SIZES.map((def) => {
                const selected = parsed?.id === def.id;
                const [w, h] = landscape ? [def.hMm, def.wMm] : [def.wMm, def.hMm];
                return (
                    <button
                        key={def.id}
                        type="button"
                        role="radio"
                        disabled={disabled}
                        aria-checked={selected}
                        onClick={() => onSelect(sizeKey(def.id, landscape))}
                        className={cn(
                            'group flex w-full items-center gap-3 rounded-xl border-2 p-1.5 pr-3 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60',
                            selected
                                ? 'border-primary bg-primary/5 shadow-sm'
                                : 'border-zinc-200 bg-white hover:border-primary/50 hover:shadow-sm dark:border-zinc-700 dark:bg-zinc-900',
                        )}
                    >
                        <PrintPreview w={w} h={h} selected={selected} />
                        <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                                <span className={cn('text-base font-bold leading-none', selected ? 'text-primary' : 'text-zinc-800 dark:text-zinc-100')}>{def.label}</span>
                                {def.id === '4r' && (
                                    <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                                        {t('studioEditor.sizePopular')}
                                    </span>
                                )}
                            </span>
                            <span className="mt-1 block text-xs font-medium text-zinc-600 dark:text-zinc-300">{t(`studioEditor.sizeUse_${def.id}`)}</span>
                            <span className="mt-0.5 block text-[11px] tabular-nums text-zinc-400 dark:text-zinc-500">
                                {num(Math.round(w))} × {num(Math.round(h))} mm · {num(inches(w))} × {num(inches(h))} in
                            </span>
                        </span>
                        <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors', selected ? 'border-primary bg-primary text-white' : 'border-zinc-300 group-hover:border-primary/50 dark:border-zinc-600')}>
                            {selected && <IconCheck size={11} strokeWidth={3.5} />}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/** The size popup (opened from the photo panel's size chip). Picking a size closes it. */
export function SizeDialog({ open, onOpenChange, size, onSelect, onOrientation }) {
    const { t } = useTranslation();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="z-[200] max-h-[90vh] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl p-4 sm:max-w-2xl sm:p-6" overlayClassName="z-[200]" closeClassName="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500 text-white opacity-100 hover:bg-red-600" aria-describedby={undefined}>
                {/* Title + description, with the portrait / landscape switch on the right (clear of the close ✕) */}
                <DialogHeader className="mb-3 flex-row flex-wrap items-start gap-x-4 gap-y-2 space-y-0 pr-8">
                    <div className="min-w-0 flex-1">
                        <DialogTitle className="text-base">{t('studioEditor.chooseSize')}</DialogTitle>
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('studioEditor.chooseSizeDesc')}</p>
                    </div>
                    <OrientationToggle fit landscape={!!parseSize(size)?.landscape} onChange={(isLandscape) => onOrientation?.(isLandscape)} />
                </DialogHeader>
                <SizeOptions size={size} className="sm:grid-cols-2 sm:gap-2.5" onSelect={(key) => { onSelect(key); onOpenChange(false); }} />
            </DialogContent>
        </Dialog>
    );
}

const groupLabelCls = 'mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400';

const swatchCls = (selected, disabled, wide = false) => cn(
    'relative w-full rounded-lg border-2 transition-colors',
    wide ? 'aspect-[4/3]' : 'aspect-square',
    selected ? 'border-primary' : 'border-zinc-200 hover:border-primary/60 dark:border-zinc-700',
    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
);

/**
 * Background kinds as tabs — colours (the solid swatches + a custom colour), gradients, patterns and scenes (bgPresets.js).
 * `bg` is a hex colour, a picker token or KEEP_BG ("keep the photo's own background"); `onPick(value)` gets the same kind of value.
 * `blend` ('' | 'seamless' | 'strong') merges the picked background into the photo's own one instead of replacing it.
 */
export function BackgroundPicker({ bg, onPick, blend = '', onBlendChange, disabled = false }) {
    const { t } = useTranslation();
    // The category chips that fit on one row stay as chips; the rest go into a separate "more" dropdown at its end. A category
    // picked from that dropdown comes out as its own chip (in front of "more"), so the row always shows the picked one.
    // Chip widths are measured from an invisible copy, re-measured on resize.
    const catKeys = useMemo(() => ['all', ...SCENE_CATEGORIES.map((c) => c.key)], []);
    const catLabel = (key) => (key === 'all' ? t('studioEditor.sceneCat_all') : t(`studioEditor.sceneCat_${key}`));
    const catRowRef = useRef(null);
    const catMeasureRef = useRef(null);
    const [catLayout, setCatLayout] = useState(null); // { widths, moreW, avail }
    // The scene tab's category filter: 'all' lists every category under its own heading.
    const [cat, setCat] = useState('all');
    const [tab, setTab] = useState(() => (isBgToken(bg) || (isHex(bg) && String(bg).toLowerCase() !== '#ffffff') ? bgTabOf(bg) : 'scene'));
    // A token that arrives from elsewhere (a reopened image) brings its own tab forward.
    useEffect(() => { if (isBgToken(bg)) setTab(bgTabOf(bg)); }, [bg]);
    const active = BG_TABS.find((x) => x.key === tab) || BG_TABS[0];
    useLayoutEffect(() => {
        const row = catRowRef.current;
        const measure = catMeasureRef.current;
        if (!row || !measure) return undefined;
        const calc = () => {
            const widths = [...measure.children].map((el) => el.offsetWidth);
            const moreW = widths.pop(); // the last measured item is the "more" button
            const avail = row.clientWidth;
            if (!avail) return;
            setCatLayout((prev) => (prev && prev.avail === avail && prev.moreW === moreW && prev.widths.join() === widths.join() ? prev : { widths, moreW, avail }));
        };
        calc();
        const ro = new ResizeObserver(calc);
        ro.observe(row);
        return () => ro.disconnect();
    }, [active.key, catKeys, t]);
    // Pattern thumbnails are painted into data URLs — build them once per tab, not on every render.
    const tileStyles = useMemo(() => Object.fromEntries((active.items || []).map((item) => [item.token, studioBgCss(item.token)])), [active]);
    const value = String(bg || '').toLowerCase();
    const solidMatch = SOLID_BACKGROUNDS.some((s) => s.hex.toLowerCase() === value);
    const customActive = isHex(bg) && !solidMatch;

    const keep = bg === KEEP_BG;
    // Tiles toggle: clicking the picked one again un-picks it — the photo then keeps its own background (the switch turns off).
    const togglePick = (value, selected) => onPick(selected ? KEEP_BG : value);
    // Tiles of the open tab, in labelled groups: scenes = one group per category (or just the picked one), colour = its gradients, pattern = one group.
    const groups = active.key === 'scene'
        ? SCENE_CATEGORIES.filter((c) => cat === 'all' || c.key === cat).map((c) => ({
            key: c.key,
            label: cat === 'all' ? t(`studioEditor.sceneCat_${c.key}`) : null,
            items: active.items.filter((i) => i.cat === c.key),
        }))
        : [{ key: active.key, label: active.key === 'color' ? t('studioEditor.bgGroup_gradient') : null, items: active.items }];
    // The last real background, so switching "keep original" off brings it back.
    const lastBgRef = useRef(keep ? '#FFFFFF' : bg);
    useEffect(() => { if (bg && bg !== KEEP_BG) lastBgRef.current = bg; }, [bg]);

    return (
        <div>
            {/* "Change the background" switch — off (the default) keeps the photo's own background and locks the choices below;
                on unlocks them, starting from the background picked before (white the first time). */}
            <div className={cn('mb-2.5 flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors', !keep ? 'border-primary bg-primary/5' : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800')}>
                <IconPhotoEdit size={17} strokeWidth={1.8} className={cn('shrink-0', !keep ? 'text-primary' : 'text-zinc-400')} />
                <label htmlFor="studio-change-bg" className={cn('min-w-0 flex-1 cursor-pointer text-[13px] font-semibold', !keep ? 'text-primary' : 'text-zinc-700 dark:text-zinc-200')}>
                    {t('studioEditor.changeBg')}
                </label>
                <Switch id="studio-change-bg" checked={!keep} disabled={disabled} onCheckedChange={(change) => onPick(change ? lastBgRef.current : KEEP_BG)} />
            </div>
            {/* Merge with the photo's own background — only while a new background is on: the photo keeps its place and the picked
                theme (a wedding stage, a garden…) is blended into it instead of replacing it. Two strengths once it is on. */}
            {!keep && onBlendChange && (
                <div className={cn('mb-2.5 rounded-lg border px-2.5 py-1.5 transition-colors', blend ? 'border-primary bg-primary/5' : 'border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800')}>
                    <div className="flex items-center gap-2">
                        <IconBlendMode size={17} strokeWidth={1.8} className={cn('shrink-0', blend ? 'text-primary' : 'text-zinc-400')} />
                        <label htmlFor="studio-bg-blend" className={cn('min-w-0 flex-1 cursor-pointer text-[13px] font-semibold', blend ? 'text-primary' : 'text-zinc-700 dark:text-zinc-200')}>
                            {t('studioEditor.bgBlend')}
                        </label>
                        <Switch id="studio-bg-blend" checked={!!blend} disabled={disabled} onCheckedChange={(on) => onBlendChange(on ? BG_BLENDS[0] : '')} />
                    </div>
                    <p className="mt-1 text-xs leading-snug text-zinc-500 dark:text-zinc-400">{t('studioEditor.bgBlendHint')}</p>
                    {blend && (
                        <div className="mt-2 grid grid-cols-2 gap-0.5 rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800/60" role="radiogroup" aria-label={t('studioEditor.bgBlend')}>
                            {BG_BLENDS.map((level) => (
                                <button
                                    key={level}
                                    type="button"
                                    role="radio"
                                    aria-checked={blend === level}
                                    disabled={disabled}
                                    onClick={() => onBlendChange(level)}
                                    className={cn(
                                        'rounded-md py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed',
                                        blend === level ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200',
                                    )}
                                >
                                    {t(`studioEditor.bgBlend_${level}`)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* While the background is kept, the new-background choices are faded — but a click on any of them turns the switch on and picks it. */}
            <fieldset disabled={disabled} className={cn('min-w-0 transition-opacity', keep && 'opacity-45 hover:opacity-80')}>
            <div className="mb-3 grid grid-cols-3 gap-0.5 rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800/60" role="tablist">
                {BG_TABS.map((x) => (
                    <button
                        key={x.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === x.key}
                        onClick={() => setTab(x.key)}
                        className={cn(
                            'truncate rounded-md px-1 py-1 text-xs font-semibold transition-colors',
                            tab === x.key ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200',
                        )}
                    >
                        {t(`studioEditor.bgTab_${x.key}`)}
                    </button>
                ))}
            </div>
            {active.key === 'scene' && (() => {
                // The picked chip and the "more" button always show; the other chips fill the row in order, a chip too wide
                // for the space left is skipped so a shorter one after it can still use the room.
                let shown = catKeys;
                if (catLayout) {
                    const { widths, moreW, avail } = catLayout;
                    const GAP = 6;
                    const total = widths.reduce((sum, w) => sum + w, 0) + GAP * (widths.length - 1);
                    if (total > avail) {
                        let used = moreW + GAP + (widths[catKeys.indexOf(cat)] || 0);
                        shown = catKeys.filter((key, i) => {
                            if (key === cat) return true;
                            if (used + GAP + widths[i] > avail) return false;
                            used += GAP + widths[i];
                            return true;
                        });
                    }
                }
                const rest = catKeys.filter((key) => !shown.includes(key));
                const chipCls = (on) => cn(
                                'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                                on ? 'border-primary bg-primary text-white' : 'border-zinc-200 bg-white text-zinc-600 hover:border-primary hover:text-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
                            );
                return (
                    <div className="relative mb-3">
                        {/* invisible copy of every chip + the "more" button, only for measuring their widths */}
                        <div ref={catMeasureRef} aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 flex gap-1.5">
                            {catKeys.map((key) => <span key={key} className={chipCls(false)}>{catLabel(key)}</span>)}
                            <span className={cn(chipCls(false), 'inline-flex items-center gap-1')}>{t('studioEditor.sceneCatMore')}<IconChevronDown size={13} /></span>
                        </div>
                        <div ref={catRowRef} className="flex gap-1.5 overflow-hidden" role="tablist" aria-label={t('studioEditor.bgTab_scene')}>
                            {shown.map((key) => (
                                <button key={key} type="button" role="tab" aria-selected={cat === key} onClick={() => setCat(key)} className={chipCls(cat === key)}>
                                    {catLabel(key)}
                                </button>
                            ))}
                            {rest.length > 0 && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button type="button" className={cn(chipCls(false), 'inline-flex items-center gap-1')}>
                                            {t('studioEditor.sceneCatMore')}
                                            <IconChevronDown size={13} />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="z-[2000] max-h-72 min-w-[180px] overflow-y-auto">
                                        {rest.map((key) => (
                                            <DropdownMenuItem key={key} onClick={() => setCat(key)} className="cursor-pointer gap-2 text-sm">
                                                <span className="flex-1">{catLabel(key)}</span>
                                                {cat === key && <IconCheck size={14} className="text-primary" />}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    </div>
                );
            })()}
            {active.key === 'color' && (
                <>
                <p className={groupLabelCls}>{t('studioEditor.bgGroup_solid')}</p>
                <div className="grid grid-cols-6 gap-2">
                    {SOLID_BACKGROUNDS.map((s) => {
                        const selected = s.hex.toLowerCase() === value;
                        return (
                            <Tooltip key={s.hex}>
                                <TooltipTrigger asChild>
                                    <button type="button" disabled={disabled} aria-pressed={selected} aria-label={t(s.labelKey)} onClick={() => togglePick(s.hex, selected)} className={swatchCls(selected, disabled)} style={{ background: s.hex }}>
                                        {selected && <CheckDot />}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="z-[2000]">{t(s.labelKey)}</TooltipContent>
                            </Tooltip>
                        );
                    })}
                    {/* Any colour: the swatch shows the picked colour once chosen. */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <label
                                className={cn(swatchCls(customActive, disabled), 'flex items-center justify-center bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600')}
                                style={customActive ? { background: bg } : undefined}
                            >
                                <IconPalette size={16} className="text-white drop-shadow" />
                                {customActive && <CheckDot />}
                                <input
                                    type="color"
                                    value={customActive ? bg : '#000000'}
                                    aria-label={t('imageGeneration.customColor')}
                                    disabled={disabled}
                                    onChange={(e) => onPick(e.target.value)}
                                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                                />
                            </label>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.customColor')}</TooltipContent>
                    </Tooltip>
                </div>
                </>
            )}
            {groups.map((group, gi) => (
                <div key={group.key} className={cn((gi > 0 || active.key === 'color') && 'mt-3.5')}>
                {group.label && <p className={groupLabelCls}>{group.label}</p>}
                <div className="grid grid-cols-4 gap-2">
                    {group.items.map((item) => {
                        const selected = bg === item.token;
                        return (
                            <Tooltip key={item.token}>
                                <TooltipTrigger asChild>
                                    <button type="button" disabled={disabled} aria-pressed={selected} aria-label={t(`studioEditor.bg_${item.id}`)} onClick={() => togglePick(item.token, selected)} className={swatchCls(selected, disabled, true)} style={tileStyles[item.token]}>
                                        {selected && <CheckDot />}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="z-[2000]">{t(`studioEditor.bg_${item.id}`)}</TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
                </div>
            ))}
            </fieldset>
        </div>
    );
}

/** One person / two people / a group as one compact segmented switch (top of the dress section) — a group keeps its clothes. */
/** A group's dress tabs: one picker below, for all the men, women or children. A dot marks a tab with a dress chosen. */
export function GroupDressTabs({ value, onChange, chosen = {}, disabled = false }) {
    const { t } = useTranslation();
    return (
        <div className="grid grid-cols-3 gap-0.5 rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800/60" role="tablist">
            {['men', 'women', 'children'].map((key) => {
                const selected = value === key;
                return (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        disabled={disabled}
                        onClick={() => onChange(key)}
                        className={cn(
                            'relative inline-flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                            selected ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200',
                        )}
                    >
                        {t(`studioEditor.group_${key}`)}
                        {chosen[key] && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                    </button>
                );
            })}
        </div>
    );
}

/** The free-text instruction: plain words only, the same limits as the AI photo editor. Code is refused whole, with a message. */
export function CustomPromptField({ value, onChange, disabled = false }) {
    const { t, i18n } = useTranslation();
    const [notice, setNotice] = useState(null); // 'blocked' = code was refused, 'filtered' = some characters were removed
    const words = value.trim().split(/\s+/).filter(Boolean).length;
    const change = (raw) => {
        if (hasCommandContent(raw)) { setNotice('blocked'); return; }
        const clean = sanitizePhotoPrompt(raw);
        setNotice(clean === raw ? null : 'filtered');
        onChange(clean);
    };
    return (
        <div>
            <label htmlFor="studio-custom-prompt" className="sr-only">{t('imageGeneration.enableCustomInstruction')}</label>
            <textarea
                id="studio-custom-prompt"
                value={value}
                onChange={(e) => change(e.target.value)}
                maxLength={PHOTO_PROMPT_MAX_CHARS}
                rows={3}
                disabled={disabled}
                placeholder={t('imageGeneration.extraEditingGuidePlaceholder')}
                className="w-full resize-none rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
            />
            <div className="mt-1 flex items-start justify-between gap-2">
                <p className={cn('text-xs', notice ? '!text-red-600 dark:!text-red-400' : '!text-zinc-400 dark:!text-zinc-500')} role={notice ? 'alert' : undefined}>
                    {notice === 'blocked' ? t('studioEditor.promptBlocked') : notice === 'filtered' ? t('studioEditor.promptFiltered') : t('imageGeneration.extraEditingGuideHint')}
                </p>
                <span className="shrink-0 text-xs tabular-nums !text-zinc-400 dark:!text-zinc-500">{numFor(i18n.language)(value.length)}/{numFor(i18n.language)(PHOTO_PROMPT_MAX_CHARS)} · {numFor(i18n.language)(words)}/{numFor(i18n.language)(PHOTO_PROMPT_MAX_WORDS)}</span>
            </div>
        </div>
    );
}

export function PeopleSelector({ value, onChange, disabled = false }) {
    const { t } = useTranslation();
    const options = [
        { key: 'single', Icon: IconUser,       label: t('studioEditor.peopleSingle') },
        { key: 'dual',   Icon: IconUsers,      label: t('studioEditor.peopleDual') },
        { key: 'group',  Icon: IconUsersGroup, label: t('studioEditor.peopleGroup') },
    ];
    return (
        <div>
            <div className="grid grid-cols-3 gap-0.5 rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800/60" role="radiogroup">
                {options.map(({ key, Icon, label }) => {
                    const selected = value === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="radio"
                            disabled={disabled}
                            aria-checked={selected}
                            onClick={() => onChange(key)}
                            className={cn(
                                'inline-flex items-center justify-center gap-1 rounded-md px-1 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                                selected ? 'bg-white text-primary shadow-sm dark:bg-zinc-700' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200',
                            )}
                        >
                            <Icon size={14} strokeWidth={1.8} />
                            {label}
                        </button>
                    );
                })}
            </div>
            {value === 'group' && (
                // A group's dress works differently from one or two people: one choice each for the men, women and children
                <div role="note" className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
                    <IconAlertTriangle size={16} className="mt-px shrink-0 text-amber-500" />
                    <p className="text-xs leading-snug !text-amber-800 dark:!text-amber-300">{t('studioEditor.peopleGroupHint')}</p>
                </div>
            )}
        </div>
    );
}
